import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminRoofSubstructure,
  InsertRoofSubstructureBody,
  UpdateRoofSubstructureNewImageBody,
  UpdateRoofSubstructureRemoveImageBody,
} from "./types/roofSubstructures";

export const useRoofSubstructuresApi = () => {
  const api = useApi();

  const getAdminRoofSubstructure = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/roof_substructures`;
      return useSWRMutation<AdminRoofSubstructure, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminRoofSubstructure>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminRoofSubstructures = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/patrol/roof_substructures", query);
      return useSWR<Paginated<AdminRoofSubstructure>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminRoofSubstructure>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminRoofSubstructure = useCallback(
    async (body: InsertRoofSubstructureBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/roof_substructures`;
      const id = await api.post<{ id: string }, InsertRoofSubstructureBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminRoofSubstructure = useCallback(
    async (
      roofSubstructureId: string,
      body: UpdateRoofSubstructureRemoveImageBody | UpdateRoofSubstructureNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/patrol/roof_substructures/${roofSubstructureId}`;
      (await api.put<UpdateRoofSubstructureRemoveImageBody | UpdateRoofSubstructureNewImageBody>(url, body),
        mutateCache(["/admin/settings/patrol/roof_substructures"]));
    },
    [api],
  );

  const deleteAdminRoofSubstructure = useCallback(
    async (roofSubstructureId: string): Promise<void> => {
      const url = `/admin/settings/patrol/roof_substructures/${roofSubstructureId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/roof_substructures`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (roofSubstructureId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/roof_substructures/${roofSubstructureId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminRoofSubstructureVisibility = useCallback(
    async (roofSubstructureId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_substructures/${roofSubstructureId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/roof_substructures"]);
    },
    [api],
  );

  const updateAdminRoofSubstructureOrder = useCallback(
    async (roofSubstructureId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/roof_substructures/${roofSubstructureId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/roof_substructures"]);
    },
    [api],
  );

  return {
    getAdminRoofSubstructure,
    useAdminRoofSubstructures,
    insertAdminRoofSubstructure,
    updateAdminRoofSubstructure,
    updateAdminRoofSubstructureVisibility,
    updateAdminRoofSubstructureOrder,
    deleteAdminRoofSubstructure,
    generateUploadImageUrl,
  };
};
